import React from 'react';
import swal from 'sweetalert';
import { API_URL } from '../constant/API_Settings';
import { formatOperationalDate } from './openingBalanceDate';

/**
 * Display report / statement dates as DD-MM-YYYY.
 * Date filter inputs stay Y-m-d; only use this for visible labels and table cells.
 */
export function formatReportDate(value) {
  return formatOperationalDate(value) || '';
}

/** Format a from–to period for report headers (e.g. "20-08-2026 — 22-08-2026"). */
export function formatReportDateRange(start, end, separator = ' — ') {
  const from = formatReportDate(start);
  const to = formatReportDate(end);
  if (!from && !to) {
    return '';
  }
  if (!from) {
    return to;
  }
  if (!to) {
    return from;
  }
  return `${from}${separator}${to}`;
}

/**
 * Parse a report field into a finite number, or null when empty / non-numeric.
 */
export function parseReportNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * Format amounts using Indian grouping (12,34,567.89) with exactly two decimal places.
 * Matches backend number_format_indian / PHP number_format(..., 2) behaviour.
 */
export function reportNumberFormat(value, options = {}) {
  const { empty = '', useCeil = false } = options;
  const num = parseReportNumber(value);

  if (num === null) {
    return empty;
  }

  const scaled = useCeil
    ? Math.ceil(num * 100) / 100
    : Math.round(num * 100) / 100;

  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(scaled);
}

/** ₹ prefix + Indian grouping with exactly two decimal places. */
export function formatInrAmount(value, options = {}) {
  const { empty = '—', symbol = true } = options;
  const formatted = reportNumberFormat(value, { empty: '' });
  if (formatted === '') {
    return empty;
  }
  return symbol ? `₹${formatted}` : formatted;
}

/** General numeric display with Indian grouping (default 2 decimals). */
export function formatIndianNumber(value, digits = 2, empty = '') {
  const num = parseReportNumber(value);
  if (num === null) {
    return empty;
  }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(num);
}

/** Percentage with Indian grouping on the numeric portion. */
export function formatIndianPercent(value, digits = 2, empty = '') {
  const formatted = formatIndianNumber(value, digits, '');
  if (formatted === '') {
    return empty;
  }
  return `${formatted}%`;
}

/** Chart / dashboard aliases — same rules as report tables. */
export const formatCurrency = formatInrAmount;

export function formatNumber(value, digits = 2) {
  return formatIndianNumber(value, digits);
}

export function formatLitres(value) {
  const formatted = formatIndianNumber(value, 2, '');
  return formatted === '' ? '' : `${formatted} L`;
}

/** Compact axis labels for charts (still uses 2-decimal Indian grouping). */
export function formatCompactInrAxis(value) {
  const num = parseReportNumber(value);
  if (num === null) {
    return '';
  }
  const abs = Math.abs(num);
  if (abs >= 10000000) {
    return `₹${formatIndianNumber(num / 10000000, 2)} Cr`;
  }
  if (abs >= 100000) {
    return `₹${formatIndianNumber(num / 100000, 2)} L`;
  }
  if (abs >= 1000) {
    return `₹${formatIndianNumber(num / 1000, 2)} K`;
  }
  return formatInrAmount(num);
}

/** CSS class for right-aligned numeric table cells. */
export const REPORT_AMOUNT_CLASS = 'report-amount';

/** CSS class for right-aligned numeric column headers. */
export const REPORT_AMOUNT_HEADER_CLASS = 'report-amount-header';

/**
 * Table cell with Indian-formatted amount (2 decimals, right aligned).
 */
export function ReportAmount({ value, className = '', children, ...props }) {
  const content = children ?? reportNumberFormat(value, { empty: '' });

  return (
    <td
      className={[REPORT_AMOUNT_CLASS, className].filter(Boolean).join(' ')}
      {...props}
    >
      {content}
    </td>
  );
}

export const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + localStorage.getItem('AUTH_TOKEN'),
  Accept: 'application/json',
});

export async function fetchReportJson(url, body) {
  const response = await fetch(url, {
    method: body ? 'post' : 'get',
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    throw new Error(response.ok ? 'Unable to load report.' : `Request failed (${response.status}). Try a shorter date range or add filters.`);
  }

  if (!response.ok || data.status === false) {
    throw new Error(data.message || 'Unable to load report.');
  }

  return data;
}

export async function downloadReportPdf(url, body, filename = 'report.pdf') {
  const response = await fetch(url, {
    method: 'post',
    headers: {
      ...authHeaders(),
      Accept: 'application/pdf, application/octet-stream, application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = 'Unable to download PDF.';
    try {
      const data = await response.json();
      message = data.message || message;
    } catch (error) {
      // response is not JSON
    }
    throw new Error(message);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    if (data.queued) {
      notifyReportDownloadsRefresh();
      swal(
        'PDF export',
        data.message || 'PDF is generating. Open the download icon in the header when it is ready.',
        'success'
      );
      return { queued: true, message: data.message };
    }
    throw new Error(data.message || 'Unable to download PDF.');
  }

  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
  return { queued: false };
}

export const REPORT_DOWNLOADS_REFRESH_EVENT = 'petrosmart-report-downloads-refresh';

export function notifyReportDownloadsRefresh() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(REPORT_DOWNLOADS_REFRESH_EVENT));
}

export async function markReportNotificationRead(id) {
  if (!id) {
    return;
  }
  await fetch(`${API_URL}/report-downloads/${id}/read`, {
    method: 'post',
    headers: authHeaders(),
  });
}

export function formatInboxDateTime(value) {
  if (!value) {
    return '';
  }
  if (/^\d{2}-\d{2}-\d{4}/.test(String(value))) {
    return String(value);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const pad = (n) => String(n).padStart(2, '0');
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(hours)}:${minutes} ${ampm}`;
}

export function inboxGeneratedMeta(item) {
  const when = item?.generated_at || formatInboxDateTime(item?.created_at);
  const who = String(item?.generated_by || '').trim();

  return { when, who };
}

export function isInboxUnread(item) {
  return Number(item?.status) === 1;
}

export const INBOX_HOVER_READ_MS = 350;

export function excludeLocallyReadDownloads(items, count, readIds) {
  const ids = readIds instanceof Set ? readIds : new Set();
  const list = (items || []).filter((row) => !ids.has(row.id));
  const hidden = (items || []).length - list.length;

  return {
    items: list,
    count: Math.max(0, (Number(count) || 0) - hidden),
  };
}
